import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  FileText,
  Users,
  Settings,
  ScrollText,
  LogOut,
  PlusCircle,
  Clock,
  ClipboardList,
  MapPin,
  FilePlus2,
  Boxes,
  Building2,
  BarChart3,
  Award,
  Palette,
  Sparkles,
  Bot,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const navItem =
  "group flex items-center gap-3 rounded-md px-3 py-2 text-sm text-ink-500 transition-colors duration-200 hover:bg-secondary hover:text-ink-900";
const activeNav =
  "bg-gradient-to-r from-ink-900 to-ink-700 text-white hover:from-ink-900 hover:to-ink-700 hover:text-white shadow-sm";

function Item({ to, icon: Icon, label, testId }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `${navItem} ${isActive ? activeNav : ""}`}
      data-testid={testId}
    >
      <Icon className="h-4 w-4" /> {label}
    </NavLink>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = React.useState({ app_name: "HPM Integrasi", app_tagline: "Sistem Surat & Absensi", app_logo: null });

  React.useEffect(() => {
    const load = () => api.get("/settings/branding").then(r => setBrand(r.data)).catch(()=>{});
    load();
    const onUpdate = () => load();
    window.addEventListener("branding-updated", onUpdate);
    return () => window.removeEventListener("branding-updated", onUpdate);
  }, []);

  async function handleLogout() {
    await logout();
    toast.success("Berhasil keluar");
    navigate("/login", { replace: true });
  }

  const role = user?.role;
  const isAdmin = role === "admin";
  const isSuratUser = role === "user";
  const isKaryawan = role === "karyawan";

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[260px_1fr] bg-background">
      {/* Sidebar */}
      <aside className="border-r border-border bg-card flex flex-col hpm-sidebar-accent" data-testid="sidebar">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            {brand.app_logo ? (
              <img src={brand.app_logo} alt="logo" className="h-9 w-9 rounded-md object-contain bg-white" />
            ) : (
              <div className="grid h-9 w-9 place-items-center rounded-md bg-gradient-to-br from-brand to-brand-700 text-white font-heading font-bold shadow-sm">{(brand.app_name || "H").charAt(0).toUpperCase()}</div>
            )}
            <div>
              <div className="font-heading text-base font-bold leading-tight tracking-tight" data-testid="brand-name">{brand.app_name || "HPM Integrasi"}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-500">{brand.app_tagline || "Sistem Surat & Absensi"}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* SURAT MENU – admin & user */}
          {(isAdmin || isSuratUser) && (
            <>
              <p className="overline px-2 mb-2">Surat</p>
              <Item to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />
              <Item to="/surat" icon={FileText} label="Rekap Surat" testId="nav-surat" />
              <Item to="/surat/baru" icon={PlusCircle} label="Buat Surat" testId="nav-buat-surat" />
              <Item to="/statistik" icon={BarChart3} label="Statistik Harian" testId="nav-statistik" />
              {isAdmin && <Item to="/report" icon={Award} label="Report & Leaderboard" testId="nav-report" />}
            </>
          )}

          {/* KARYAWAN MENU */}
          {isKaryawan && (
            <>
              <p className="overline px-2 mb-2">Karyawan</p>
              <Item to="/absensi" icon={Clock} label="Absensi" testId="nav-absensi" />
              <Item to="/izin" icon={FilePlus2} label="Izin" testId="nav-izin-karyawan" />
            </>
          )}

          {/* AI TOOLS – semua user */}
          <p className="overline px-2 mt-6 mb-2">AI Tools</p>
          <Item to="/sales-training" icon={Sparkles} label="Sales Training" testId="nav-sales-training" />
          <Item to="/ai-assistant" icon={Bot} label="AI Assistant" testId="nav-ai-assistant" />

          {/* ADMIN GROUP */}
          {isAdmin && (
            <>
              <p className="overline px-2 mt-6 mb-2">Administrasi Surat</p>
              <Item to="/users" icon={Users} label="Manajemen User" testId="nav-users" />
              <Item to="/produk" icon={Boxes} label="Produk & Syarat" testId="nav-produk" />
              <Item to="/perusahaan" icon={Building2} label="Perusahaan & Rekening" testId="nav-perusahaan" />
              <Item to="/pengaturan" icon={Settings} label="Pengaturan Penomoran" testId="nav-pengaturan" />

              <p className="overline px-2 mt-6 mb-2">Administrasi Absensi</p>
              <Item to="/rekap-absensi" icon={ClipboardList} label="Rekap Absensi" testId="nav-rekap-absensi" />
              <Item to="/pengaturan-absensi" icon={MapPin} label="Pengaturan Absensi" testId="nav-pengaturan-absensi" />
              <Item to="/izin" icon={FilePlus2} label="Izin Karyawan" testId="nav-izin-admin" />

              <p className="overline px-2 mt-6 mb-2">Sistem</p>
              <Item to="/branding" icon={Palette} label="Branding & Tampilan" testId="nav-branding" />
              <Item to="/audit" icon={ScrollText} label="Audit Log" testId="nav-audit" />
            </>
          )}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-medium text-ink-900" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-ink-500">@{user?.username} · {user?.role}</div>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="mr-2 h-4 w-4" /> Keluar
          </Button>
        </div>
      </aside>

      {/* Content */}
      <main className="min-w-0 p-6 lg:p-10">
        <Outlet />
      </main>
    </div>
  );
}
